import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  useGetPhdDocumentVariablesQuery,
  useSavePhdDocumentVariableMutation,
  useDeletePhdDocumentVariableMutation,
} from '../../../../../../redux/services/phd.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import checkPermission from '../../../../../../utils/helpers/checkPermission';
import useIsUserPhdAdmin from '../../../../../../hooks/useIsUserPhdAdmin.jsx';
import { Spinner } from '../../../../../common';
import PhdSettingsSection from './PhdSettingsSection.jsx';
import PhdHelpHint from '../../../../../common/Phd/PhdHelpHint.jsx';

const KEY_PATTERN = /^[A-Za-z0-9_.-]+$/;

const PhdDocumentVariablesSection = ({ prefillKey, onPrefillHandled }) => {
  const { t } = useTranslation();
  const { data: variables, isLoading } = useGetPhdDocumentVariablesQuery();
  const [saveVariable, { isLoading: isSaving }] =
    useSavePhdDocumentVariableMutation();
  const [deleteVariable] = useDeletePhdDocumentVariableMutation();
  const { saveSuccess, saveFailed } = useToastMessages();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useIsUserPhdAdmin();
  const canEdit = checkPermission(user, 'phd-system-states.store', isAdmin);
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const rows = useMemo(
    () => (Array.isArray(variables) ? variables : []),
    [variables],
  );

  const openModal = (record) => {
    setEditing(record ?? null);
    form.setFieldsValue({
      key: record?.key ?? '',
      label: record?.label ?? '',
      value: record?.value ?? '',
    });
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (prefillKey && canEdit) {
      openModal(null);
      form.setFieldsValue({ key: prefillKey, label: '', value: '' });
      onPrefillHandled?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillKey]);

  const closeModal = () => {
    setIsModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const handleCopyToken = async (key) => {
    const token = `\${${key}}`;
    try {
      await navigator.clipboard.writeText(token);
      message.success(t('Skopírované: {{token}}', { token }));
    } catch (error) {
      message.info(token);
    }
  };

  const handleSubmit = async (values) => {
    if (!canEdit) {
      return;
    }

    try {
      await saveVariable({
        id: editing?.id,
        body: {
          key: values.key,
          label: values.label,
          value: values.value ?? '',
        },
      }).unwrap();
      saveSuccess();
      closeModal();
    } catch (error) {
      saveFailed(error);
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteVariable(record.id).unwrap();
      saveSuccess();
    } catch (error) {
      saveFailed(error);
    }
  };

  const columns = [
    {
      title: t('Názov'),
      dataIndex: 'label',
      key: 'label',
      render: (value) => <Typography.Text strong>{value}</Typography.Text>,
    },
    {
      title: t('Premenná'),
      dataIndex: 'key',
      key: 'key',
      width: 220,
      render: (value) => (
        <Space size={4}>
          <Typography.Text code>{`\${${value}}`}</Typography.Text>
          <Tooltip title={t('Kopírovať premennú')}>
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              onClick={() => handleCopyToken(value)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: t('Hodnota'),
      dataIndex: 'value',
      key: 'value',
      render: (value) =>
        value ? (
          <Typography.Text
            style={{ whiteSpace: 'pre-wrap' }}
            ellipsis={{ tooltip: value }}
          >
            {value}
          </Typography.Text>
        ) : (
          '—'
        ),
    },
    {
      title: t('Akcie'),
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            disabled={!canEdit}
            onClick={() => openModal(record)}
          >
            {t('Upraviť')}
          </Button>
          <Popconfirm
            title={t('Odstrániť premennú?')}
            okText={t('Odstrániť')}
            cancelText={t('Zrušiť')}
            disabled={!canEdit}
            onConfirm={() => handleDelete(record)}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              disabled={!canEdit}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <PhdSettingsSection
      title={t('Vlastné premenné dokumentov')}
      description={t(
        'Vytvorte si vlastné premenné so statickým textom (napr. meno dekana, právna doložka). Premennú vložíte do ľubovoľnej DOCX šablóny ako ${premenná} a pri generovaní sa nahradí zadanou hodnotou.',
      )}
    >
      <Space orientation="vertical" size={12} style={{ width: '100%' }}>
        <div style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canEdit}
            onClick={() => openModal(null)}
          >
            {t('Pridať premennú')}
          </Button>
        </div>
        <Table
          rowKey="id"
          dataSource={rows}
          columns={columns}
          pagination={false}
          size="small"
          locale={{
            emptyText: t('Zatiaľ nie sú vytvorené žiadne vlastné premenné.'),
          }}
        />
      </Space>

      <Modal
        title={editing ? t('Upraviť premennú') : t('Pridať premennú')}
        open={isModalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText={t('Uložiť')}
        cancelText={t('Zrušiť')}
        confirmLoading={isSaving}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="key"
            label={
              <span>
                {t('Kľúč premennej')}
                <PhdHelpHint
                  label={t('Čo je kľúč premennej?')}
                  title={t(
                    'Kľúč zapisujete do DOCX šablóny medzi ${ a }. Pri exporte sa celý token nahradí hodnotou premennej.',
                  )}
                />
              </span>
            }
            extra={t(
              'Používa sa v šablóne ako ${kľúč}. Iba písmená, číslice a znaky _ . -',
            )}
            rules={[
              { required: true, message: t('Zadajte kľúč premennej.') },
              {
                pattern: KEY_PATTERN,
                message: t(
                  'Kľúč môže obsahovať iba písmená, číslice a znaky _ . -',
                ),
              },
            ]}
          >
            <Input placeholder="dekan" addonBefore="${" addonAfter="}" />
          </Form.Item>
          <Form.Item
            name="label"
            label={t('Názov')}
            rules={[{ required: true, message: t('Zadajte názov premennej.') }]}
          >
            <Input placeholder={t('Dekan fakulty')} />
          </Form.Item>
          <Form.Item name="value" label={t('Hodnota')}>
            <Input.TextArea
              rows={4}
              placeholder={t('Text, ktorý sa vloží do dokumentu.')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </PhdSettingsSection>
  );
};

PhdDocumentVariablesSection.propTypes = {
  prefillKey: PropTypes.string,
  onPrefillHandled: PropTypes.func,
};

export default PhdDocumentVariablesSection;
